// Resize, crop and rotate, on /tools/resize.
//
// The arithmetic lives in tools/transform.js and is deliberately kept out of
// here: rotating a crop along with its picture and holding a shape to a ratio
// are the two things in this tool that are easy to get subtly wrong, and they
// are worth being able to test without a browser in the way.
//
// What is left here is the part that only makes sense with a pointer in it: a
// box with eight handles, drawn over the picture, that can be moved, resized,
// redrawn from scratch, and held to a shape while all of that happens.

import {
    ROTATIONS, ASPECTS, FULL_CROP,
    orientedSize, clampCrop, rotateCrop, flipCrop, applyAspect, largestCrop, resizeCrop,
    cropPixels, outputSize, matchingHeight, matchingWidth,
    renderTransform, renderPreviewFrame,
} from './tools/transform.js';
import { decode, sizeOf, hasAlpha, encode, renameExtension, saveBlob, formatBytes } from './tools/images.js';
import { wirePicker, blockStrayDrops, initColourPicker } from './tools/picker.js';
import { wireCopy } from './tools/copy.js';
import { wireAccountSave } from './tools/account.js';

// What the crop is drawn over. Large enough to place a box precisely, small
// enough to redraw on every pointer move without the picture lagging behind.
const PREVIEW_WIDTH = 1100;

// Browsers refuse a canvas past roughly this and answer with a blank one rather
// than an error, so the export is held under it. See tools-screenshot.js, which
// hit the same wall from the other direction.
const MAX_EXPORT_EDGE = 8192;

// How close a pointer has to come to a handle, in screen pixels rather than
// canvas pixels, so it feels the same on a scaled-down preview.
const HANDLE_GRAB = 14;

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const FORMATS = {
    'image/png':  { label: 'PNG',  extension: 'png',  quality: undefined, transparent: true },
    'image/webp': { label: 'WebP', extension: 'webp', quality: 0.92,      transparent: true },
    'image/jpeg': { label: 'JPEG', extension: 'jpg',  quality: 0.92,      transparent: false },
};

const el = id => document.getElementById(id);

const dom = {
    zone: el('resizeZone'),
    input: el('resizeInput'),
    empty: el('resizeEmpty'),
    workspace: el('resizeWorkspace'),
    stage: el('resizeStage'),
    canvas: el('resizeCanvas'),
    overlay: el('resizeOverlay'),
    rotLeft: el('resizeRotLeft'),
    rotRight: el('resizeRotRight'),
    flipH: el('resizeFlipH'),
    flipV: el('resizeFlipV'),
    aspect: el('resizeAspect'),
    cropReset: el('resizeCropReset'),
    toggle: el('resizeToggle'),
    fields: el('resizeFields'),
    width: el('resizeWidth'),
    height: el('resizeHeight'),
    lock: el('resizeLock'),
    format: el('resizeFormat'),
    backgroundRow: el('resizeBackgroundRow'),
    background: el('resizeBackground'),
    meta: el('resizeMeta'),
    error: el('resizeError'),
    save: el('resizeSave'),
    reset: el('resizeReset'),
    saveAccount: el('toolSaveAccount'),
    savePanel: el('toolSavePanel'),
    saveList: el('toolSaveList'),
    saveError: el('toolSaveError'),
};

const state = {
    image: null,
    file: null,
    transparent: false,
    rotation: 0,
    flipX: false,
    flipY: false,
    crop: { ...FULL_CROP },
    aspect: 'free',
    resize: { enabled: false, width: 0, height: 0, lock: true },
    background: '#ffffff',
};

let frame = null;        // The rotated picture at preview size.
let drag = null;         // { mode, handle, start, origin }
let hover = null;        // Handle under the pointer, for the cursor.

/* ------------------------------------------------------------- geometry --- */

function ratioOf() {
    const entry = ASPECTS.find(a => a.id === state.aspect);
    return entry ? entry.ratio : null;
}

function frameSize() {
    return orientedSize(sizeOf(state.image), state.rotation);
}

/** A pointer event as a position in the rotated frame, from 0 to 1. */
function toFrame(event) {
    const rect = dom.overlay.getBoundingClientRect();
    return {
        x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
}

/** The crop in canvas pixels of the preview. */
function cropOnCanvas() {
    return {
        x: state.crop.x * dom.overlay.width,
        y: state.crop.y * dom.overlay.height,
        w: state.crop.w * dom.overlay.width,
        h: state.crop.h * dom.overlay.height,
    };
}

function handlePoints() {
    const r = cropOnCanvas();
    return {
        nw: [r.x, r.y],
        n:  [r.x + r.w / 2, r.y],
        ne: [r.x + r.w, r.y],
        e:  [r.x + r.w, r.y + r.h / 2],
        se: [r.x + r.w, r.y + r.h],
        s:  [r.x + r.w / 2, r.y + r.h],
        sw: [r.x, r.y + r.h],
        w:  [r.x, r.y + r.h / 2],
    };
}

/** Which handle, if any, a pointer is over. */
function handleAt(event) {
    const rect = dom.overlay.getBoundingClientRect();
    if (!rect.width) return null;

    // The tolerance is a distance on screen, converted into canvas units, so
    // grabbing a corner feels identical however far the preview is scaled.
    const grab = HANDLE_GRAB * (dom.overlay.width / rect.width);
    const point = toFrame(event);
    const px = point.x * dom.overlay.width;
    const py = point.y * dom.overlay.height;

    const points = handlePoints();
    let best = null;
    let bestDistance = grab;

    for (const name of HANDLES) {
        const [hx, hy] = points[name];
        const distance = Math.hypot(px - hx, py - hy);
        if (distance <= bestDistance) {
            best = name;
            bestDistance = distance;
        }
    }
    return best;
}

function insideCrop(point) {
    const c = state.crop;
    return point.x >= c.x && point.x <= c.x + c.w && point.y >= c.y && point.y <= c.y + c.h;
}

/* -------------------------------------------------------------- drawing --- */

function drawFrame() {
    if (!state.image) return;
    frame = renderPreviewFrame(state.image, state, PREVIEW_WIDTH);

    dom.canvas.width = frame.width;
    dom.canvas.height = frame.height;
    dom.canvas.getContext('2d').drawImage(frame, 0, 0);

    dom.overlay.width = frame.width;
    dom.overlay.height = frame.height;

    // The squares behind the preview mean something only where the picture
    // actually has holes in it. Behind a photograph they are just noise.
    dom.stage.classList.toggle('is-transparent', state.transparent);
}

function drawOverlay() {
    if (!frame) return;

    const ctx = dom.overlay.getContext('2d');
    const W = dom.overlay.width;
    const H = dom.overlay.height;
    ctx.clearRect(0, 0, W, H);

    const r = cropOnCanvas();

    // Everything outside the crop is dimmed, which says what will be lost
    // better than an outline on its own does.
    ctx.fillStyle = 'rgba(2, 6, 23, .55)';
    ctx.fillRect(0, 0, W, r.y);
    ctx.fillRect(0, r.y + r.h, W, H - (r.y + r.h));
    ctx.fillRect(0, r.y, r.x, r.h);
    ctx.fillRect(r.x + r.w, r.y, W - (r.x + r.w), r.h);

    const line = Math.max(1, Math.round(W / 700));

    // Thirds, faint, for placing a subject rather than for decoration.
    ctx.strokeStyle = 'rgba(255, 255, 255, .35)';
    ctx.lineWidth = line;
    ctx.beginPath();
    for (let i = 1; i < 3; i += 1) {
        ctx.moveTo(r.x + (r.w * i) / 3, r.y);
        ctx.lineTo(r.x + (r.w * i) / 3, r.y + r.h);
        ctx.moveTo(r.x, r.y + (r.h * i) / 3);
        ctx.lineTo(r.x + r.w, r.y + (r.h * i) / 3);
    }
    ctx.stroke();

    ctx.strokeStyle = 'rgba(255, 255, 255, .95)';
    ctx.lineWidth = line * 2;
    ctx.strokeRect(r.x, r.y, r.w, r.h);

    const size = Math.max(6, Math.round(W / 130));
    const points = handlePoints();
    for (const name of HANDLES) {
        const [hx, hy] = points[name];
        ctx.fillStyle = name === hover || (drag && drag.handle === name) ? '#38bdf8' : '#ffffff';
        ctx.strokeStyle = 'rgba(2, 6, 23, .7)';
        ctx.lineWidth = line;
        ctx.beginPath();
        ctx.rect(hx - size / 2, hy - size / 2, size, size);
        ctx.fill();
        ctx.stroke();
    }
}

function paintMeta() {
    if (!state.image) return;
    const source = sizeOf(state.image);
    const cut = cropPixels(source, state);
    const out = outputSize(source, state);

    const parts = [`${source.width} x ${source.height} source`];
    if (cut.width !== source.width || cut.height !== source.height) {
        parts.push(`crop ${cut.width} x ${cut.height}`);
    }
    const saved = exportSize();
    parts.push(saved.width === out.width && saved.height === out.height
        ? `saves at ${out.width} x ${out.height}`
        : `saves at ${saved.width} x ${saved.height}, the most a browser canvas holds`);
    dom.meta.textContent = parts.join(' - ');
}

function redraw() {
    drawFrame();
    drawOverlay();
    syncFields();
    paintMeta();
}

/* ------------------------------------------------------------- controls --- */

function syncFields() {
    dom.fields.hidden = !state.resize.enabled;
    dom.backgroundRow.hidden = FORMATS[dom.format.value] ? FORMATS[dom.format.value].transparent || !state.transparent : true;

    if (!state.resize.enabled && state.image) {
        const cut = cropPixels(sizeOf(state.image), state);
        state.resize.width = cut.width;
        state.resize.height = cut.height;
        dom.width.value = cut.width;
        dom.height.value = cut.height;
    }
}

/** Keep the two size fields in step when the shape is locked. */
function onWidthInput() {
    const width = Number(dom.width.value) || 0;
    state.resize.width = width;
    if (state.resize.lock && width > 0 && state.image) {
        state.resize.height = matchingHeight(sizeOf(state.image), state, width);
        dom.height.value = state.resize.height;
    }
    paintMeta();
}

function onHeightInput() {
    const height = Number(dom.height.value) || 0;
    state.resize.height = height;
    if (state.resize.lock && height > 0 && state.image) {
        state.resize.width = matchingWidth(sizeOf(state.image), state, height);
        dom.width.value = state.resize.width;
    }
    paintMeta();
}

/** A preset sets the long edge and lets the other one follow. */
function applyLongEdge(edge) {
    if (!state.image) return;
    const cut = cropPixels(sizeOf(state.image), state);
    state.resize.enabled = true;
    dom.toggle.checked = true;

    if (cut.width >= cut.height) {
        dom.width.value = edge;
        onWidthInput();
    } else {
        dom.height.value = edge;
        onHeightInput();
    }
    syncFields();
    paintMeta();
}

function setAspect(id) {
    state.aspect = id;
    const ratio = ratioOf();
    if (ratio) {
        state.crop = applyAspect(state.crop, ratio, frameSize());
    }
    redraw();
}

function rotate(quarters) {
    state.rotation = ROTATIONS[(((ROTATIONS.indexOf(state.rotation) + quarters) % 4) + 4) % 4];
    state.crop = rotateCrop(state.crop, quarters);
    // The shape is expressed against the screen, so after a quarter turn the
    // crop has to be brought back to it rather than carried through sideways.
    const ratio = ratioOf();
    if (ratio) state.crop = applyAspect(state.crop, ratio, frameSize());
    redraw();
}

function flip(horizontal) {
    if (horizontal) state.flipX = !state.flipX;
    else state.flipY = !state.flipY;
    state.crop = flipCrop(state.crop, horizontal);
    redraw();
}

function resetCrop() {
    const ratio = ratioOf();
    state.crop = ratio ? largestCrop(ratio, frameSize()) : { ...FULL_CROP };
    redraw();
}

/* ---------------------------------------------------------- interaction --- */

function wireCropping() {
    let pointerId = null;

    dom.overlay.addEventListener('pointermove', event => {
        if (pointerId !== null) return;
        const next = handleAt(event);
        if (next !== hover) {
            hover = next;
            dom.overlay.style.cursor = next
                ? (next === 'n' || next === 's' ? 'ns-resize'
                    : next === 'e' || next === 'w' ? 'ew-resize'
                    : next === 'nw' || next === 'se' ? 'nwse-resize' : 'nesw-resize')
                : (insideCrop(toFrame(event)) ? 'move' : 'crosshair');
            drawOverlay();
        }
    });

    dom.overlay.addEventListener('pointerdown', event => {
        if (!frame || (event.button !== 0 && event.pointerType === 'mouse')) return;
        pointerId = event.pointerId;
        dom.overlay.setPointerCapture(pointerId);

        const point = toFrame(event);
        const handle = handleAt(event);

        if (handle) {
            drag = { mode: 'resize', handle, start: { ...state.crop } };
        } else if (insideCrop(point)) {
            drag = { mode: 'move', origin: point, start: { ...state.crop } };
        } else {
            drag = { mode: 'draw', origin: point, start: { ...state.crop } };
            state.crop = clampCrop({ x: point.x, y: point.y, w: 0.02, h: 0.02 });
        }
        drawOverlay();
    });

    dom.overlay.addEventListener('pointermove', event => {
        if (pointerId === null || event.pointerId !== pointerId || !drag) return;
        const point = toFrame(event);
        const ratio = ratioOf();
        const size = frameSize();

        if (drag.mode === 'resize') {
            state.crop = resizeCrop(drag.handle, point, drag.start, ratio, size);
        } else if (drag.mode === 'move') {
            state.crop = clampCrop({
                ...drag.start,
                x: drag.start.x + (point.x - drag.origin.x),
                y: drag.start.y + (point.y - drag.origin.y),
            });
        } else {
            // Drawing a new box behaves exactly like dragging its far corner,
            // so there is one piece of resizing logic rather than two.
            state.crop = resizeCrop('se', point, {
                x: drag.origin.x, y: drag.origin.y, w: 0, h: 0,
            }, ratio, size);
        }

        drawOverlay();
        syncFields();
        paintMeta();
    });

    const finish = event => {
        if (pointerId === null || (event && event.pointerId !== pointerId)) return;
        try {
            dom.overlay.releasePointerCapture(pointerId);
        } catch (err) {
            // Already released, which is the state this wanted anyway.
        }
        pointerId = null;
        drag = null;
        redraw();
    };

    dom.overlay.addEventListener('pointerup', finish);
    dom.overlay.addEventListener('pointercancel', finish);
}

/* --------------------------------------------------------------- output --- */

/** The width and height to draw at, held under what a canvas will allocate. */
function exportSize() {
    const out = outputSize(sizeOf(state.image), state);
    const over = Math.max(out.width, out.height) / MAX_EXPORT_EDGE;
    if (over <= 1) return out;
    return {
        width: Math.max(1, Math.floor(out.width / over)),
        height: Math.max(1, Math.floor(out.height / over)),
    };
}

async function renderOutput() {
    const format = FORMATS[dom.format.value] || FORMATS['image/png'];
    const size = exportSize();
    const canvas = renderTransform(state.image, state, size.width, size.height);

    // JPEG has no alpha, so anything see-through would come out as whatever the
    // encoder felt like. Filling it explicitly is the only way to know.
    let final = canvas;
    if (!format.transparent && state.transparent) {
        const filled = document.createElement('canvas');
        filled.width = canvas.width;
        filled.height = canvas.height;
        const ctx = filled.getContext('2d');
        ctx.fillStyle = state.background;
        ctx.fillRect(0, 0, filled.width, filled.height);
        ctx.drawImage(canvas, 0, 0);
        final = filled;
    }

    const blob = await encode(final, dom.format.value, format.quality);
    return {
        blob,
        name: renameExtension(state.file.name, format.extension),
        width: final.width,
        height: final.height,
    };
}

async function save() {
    if (!state.image) return;
    dom.save.disabled = true;
    setError('');
    try {
        const out = await renderOutput();
        saveBlob(out.blob, out.name);
        dom.meta.textContent = `Saved ${out.width} x ${out.height}, ${formatBytes(out.blob.size)}`;
    } catch (err) {
        setError(err.message || 'Could not render the image');
    } finally {
        dom.save.disabled = false;
    }
}

function setError(message) {
    dom.error.hidden = !message;
    dom.error.textContent = message || '';
}

/* ----------------------------------------------------------- life cycle --- */

function reset() {
    if (state.image && state.image.close) state.image.close();
    state.image = null;
    state.file = null;
    state.transparent = false;
    state.rotation = 0;
    state.flipX = false;
    state.flipY = false;
    state.crop = { ...FULL_CROP };
    state.resize.enabled = false;
    frame = null;
    drag = null;
    hover = null;

    dom.toggle.checked = false;
    dom.workspace.hidden = true;
    dom.empty.hidden = false;
    dom.meta.textContent = '';
    setError('');
}

async function open([file]) {
    if (!file) return;
    reset();

    state.file = file;
    try {
        state.image = await decode(file);
        state.transparent = hasAlpha(state.image);
    } catch (err) {
        setError(err.message || 'Could not read this image');
        return;
    }

    resetCrop();
    dom.empty.hidden = true;
    dom.workspace.hidden = false;
    redraw();
}

function fillAspects() {
    dom.aspect.innerHTML = '';
    for (const entry of ASPECTS) {
        const option = document.createElement('option');
        option.value = entry.id;
        option.textContent = entry.label;
        dom.aspect.appendChild(option);
    }
    dom.aspect.value = state.aspect;
}

function init() {
    if (!dom.zone) return;

    blockStrayDrops();
    initColourPicker();
    wirePicker({ zone: dom.zone, input: dom.input, multiple: false, onFiles: open });
    wireCropping();
    fillAspects();

    dom.rotLeft.addEventListener('click', () => rotate(-1));
    dom.rotRight.addEventListener('click', () => rotate(1));
    dom.flipH.addEventListener('click', () => flip(true));
    dom.flipV.addEventListener('click', () => flip(false));
    dom.cropReset.addEventListener('click', resetCrop);
    dom.aspect.addEventListener('change', () => setAspect(dom.aspect.value));

    dom.toggle.addEventListener('change', () => {
        state.resize.enabled = dom.toggle.checked;
        syncFields();
        paintMeta();
    });
    dom.width.addEventListener('input', onWidthInput);
    dom.height.addEventListener('input', onHeightInput);
    dom.lock.addEventListener('change', () => { state.resize.lock = dom.lock.checked; });

    for (const button of document.querySelectorAll('[data-long-edge]')) {
        button.addEventListener('click', () => applyLongEdge(Number(button.dataset.longEdge)));
    }

    dom.format.addEventListener('change', syncFields);
    dom.background.addEventListener('input', () => { state.background = dom.background.value; });

    dom.save.addEventListener('click', save);
    dom.reset.addEventListener('click', reset);
    wireCopy({ button: document.getElementById('resizeCopy'), mirror: dom.save, collect: () => (state.image ? renderOutput() : null) });

    wireAccountSave({
        button: dom.saveAccount,
        panel: dom.savePanel,
        list: dom.saveList,
        error: dom.saveError,
        collect: async () => state.image ? [await renderOutput()] : [],
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
